import * as _ from "lodash";
import { useEffect, FC, createContext, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Enterprise,
  EnterpriseAppState,
  ScorecardValueParams,
  FarmEntity,
  DataDescriptorEntity,
  Scorecard,
  ScorecardValueEntity,
  UnitSystemConversionMap
} from "@ctra/api";

import { isPending, isRejected } from "@ctra/utils";

import { useLocalization } from "@base";

interface ContextType extends Partial<ScorecardValueEntity> {
  meta: {
    isLoading: boolean;
  };
}

/**
 * Make a default context for scorecard values
 */
const DefaultContext = createContext<ContextType>({
  meta: { isLoading: true }
});

interface ProviderProps extends Partial<ScorecardValueParams> {
  farmID?: FarmEntity["id"];
  dataDescriptorID?: DataDescriptorEntity["id"];
}

/**
 * Fetch a scorecard value
 * @param farmID
 * @param dataDescriptorID
 * @param children
 * @param params
 * @private
 */
const _ScorecardValueProvider: FC<ProviderProps> = ({
  farmID,
  dataDescriptorID,
  children,
  ...defaultParams
}) => {
  const dispatch = useDispatch();
  const { unitSystem } = useLocalization();

  /**
   * Use these as the base params as it seems to repeat often
   */
  const params = useMemo(
    () => ({
      unitSystem: UnitSystemConversionMap[unitSystem],
      ...defaultParams
    }),
    [unitSystem, defaultParams]
  );

  /**
   * Get a previously fetched scorecard value
   */
  const scorecardValue = useSelector<EnterpriseAppState, Partial<ScorecardValueEntity>>((state) =>
    dataDescriptorID && farmID
      ? Enterprise.entities.getScorecardValue(state, { dataDescriptorID, farmID, ...params })
      : {}
  );

  /**
   * Tell if the value fetching call is in progress
   */
  const isLoading = useSelector<EnterpriseAppState, boolean>((state) =>
    dataDescriptorID && farmID
      ? isPending(state, Scorecard.types.FETCH_SCORECARD_VALUE, {
          primaryValue: Scorecard.utils.makeScorecardHash(dataDescriptorID, farmID, params)
        })
      : true
  );

  /**
   * Tell if the value fetching call has failed
   */
  const hasFailed = useSelector<EnterpriseAppState, boolean>((state) =>
    dataDescriptorID && farmID
      ? isRejected(state, Scorecard.types.FETCH_SCORECARD_VALUE, {
          primaryValue: Scorecard.utils.makeScorecardHash(dataDescriptorID, farmID, params)
        })
      : true
  );

  useEffect(() => {
    if (dataDescriptorID && farmID && _.isEmpty(scorecardValue) && !isLoading && !hasFailed) {
      dispatch(Scorecard.actions.fetchScorecardValue.start(dataDescriptorID, farmID, params));
    }
  }, [dispatch, scorecardValue, isLoading, hasFailed, dataDescriptorID, farmID, params]);

  return (
    <DefaultContext.Provider
      value={{
        ...scorecardValue,
        meta: {
          isLoading
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const ScorecardContext = {
  Provider: _ScorecardValueProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Make a hook to access the context in FCs
 */
export const useScorecard = (): ContextType => useContext(DefaultContext);
